import { Component, type ErrorInfo, type ReactNode } from 'react';
import { DefaultErrorFallback } from './default-error-fallback';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Optional custom fallback renderer. If omitted, a minimal i18n'd error
   * card renders with a retry button that re-mounts the children.
   */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * React error boundary. Must be a class component — hooks cannot catch
 * render-phase errors.
 *
 * The default fallback uses `t()` from the shared i18n provider, so this
 * boundary MUST be rendered below an `<I18nProvider>` in the tree. See
 * `apps/<app>/src/app.tsx` for the canonical nesting order:
 *   i18n > query > error-boundary > router
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log the error with context so it surfaces in dev consoles and, once
    // wired, in our production telemetry. Do not swallow it.
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error !== null) {
      if (this.props.fallback) {
        return this.props.fallback(error, this.reset);
      }
      return <DefaultErrorFallback reset={this.reset} />;
    }
    return this.props.children;
  }
}
